/**
 * 使い方を聞く: 分類 → 質問 → 答え（使い方のマスター）
 *
 * 木の答えは AI を使わずにそのまま見せる（予算は減らない）。
 * 木に無い質問は、入力すると分類の答えを根拠に AI が答える（親が実行する）。
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import AiMark from '@/components/chat/AiMark';
import { DisabledReason } from '@/components/common/DisabledReason';
import { HELP_MASTER, type HelpCategory, type HelpItem } from '@/constants/helpMaster';

const QUESTION_MAX_LENGTH = 300;

interface ChatHelpTreeProps {
  busy: boolean;
  onViewAnswer: (item: HelpItem) => void;
  onAsk: (categoryId: string, question: string) => void;
  onNavigate: () => void;
  /** 階層が変わったとき（パネルがスクロール位置を階層ごとに覚える） */
  onLevelChange?: (levelKey: string) => void;
}

function AskForm({
  categoryId,
  busy,
  onAsk,
}: {
  categoryId: string;
  busy: boolean;
  onAsk: (categoryId: string, question: string) => void;
}) {
  const [question, setQuestion] = useState('');
  const trimmed = question.trim();
  const reason = !trimmed
    ? '聞きたいことを入れてください'
    : question.length > QUESTION_MAX_LENGTH
      ? `${QUESTION_MAX_LENGTH}文字以内にしてください`
      : null;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason && !busy) onAsk(categoryId, trimmed);
      }}
      className="space-y-2 rounded-xl bg-text-50 p-3"
    >
      <label htmlFor="help-question" className="text-xs text-text-600">
        見つからないときは、AIに聞けます（今月の利用量を使います）
      </label>
      <textarea
        id="help-question"
        rows={2}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        className="w-full resize-none rounded-lg border border-text-200 bg-white px-3 py-2 text-base focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
      />
      <button
        type="submit"
        disabled={!!reason || busy}
        aria-describedby="help-question-reason"
        className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-text-200 disabled:text-text-400"
      >
        <AiMark className="text-current" />
        AIに聞く
      </button>
      <DisabledReason id="help-question-reason" reason={busy ? null : reason} align="left" />
    </form>
  );
}

export default function ChatHelpTree({
  busy,
  onViewAnswer,
  onAsk,
  onNavigate,
  onLevelChange,
}: ChatHelpTreeProps) {
  const [category, setCategory] = useState<HelpCategory | null>(null);
  const [item, setItem] = useState<HelpItem | null>(null);

  const back = () => {
    if (item) {
      setItem(null);
      onLevelChange?.(category?.id ?? '');
    } else {
      setCategory(null);
      onLevelChange?.('');
    }
  };

  const openCategory = (next: HelpCategory) => {
    setCategory(next);
    onLevelChange?.(next.id);
  };

  const open = (next: HelpItem) => {
    setItem(next);
    onLevelChange?.(`${category?.id ?? ''}/${next.id}`);
    onViewAnswer(next);
  };

  return (
    <div className="space-y-3">
      {/* いちばん上の段では、ヘッダーの戻る（ホームへ）と同じになるので出さない */}
      {category && (
        <button
          type="button"
          onClick={back}
          className="flex items-center gap-0.5 rounded-lg py-1 pr-2 text-xs text-text-500 hover:text-text-800"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          戻る
        </button>
      )}

      {!category && (
        <ul className="space-y-2">
          {HELP_MASTER.categories.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => openCategory(entry)}
                className="flex w-full items-center rounded-xl border border-text-200 px-3 py-2.5 text-left hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex-1">
                  <span className="block text-sm font-medium text-text-900">{entry.label}</span>
                  {entry.description && (
                    <span className="block text-[11px] text-text-500">{entry.description}</span>
                  )}
                </span>
                <ChevronRightIcon className="h-4 w-4 text-text-400" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {category && !item && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-text-900">{category.label}</p>
          <ul className="divide-y divide-dashed divide-text-100">
            {category.items.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => open(entry)}
                  className="flex w-full items-center py-2.5 text-left text-sm text-text-800 hover:text-primary-700"
                >
                  <span className="flex-1">{entry.question}</span>
                  <ChevronRightIcon className="h-4 w-4 text-text-400" />
                </button>
              </li>
            ))}
          </ul>
          <AskForm categoryId={category.id} busy={busy} onAsk={onAsk} />
        </div>
      )}

      {category && item && (
        <article className="space-y-3">
          <h3 className="text-sm font-semibold text-text-900">{item.question}</h3>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-800">{item.answer}</p>
          {(item.links ?? []).length > 0 && (
            <ul className="space-y-1">
              {(item.links ?? []).map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={onNavigate}
                    className="text-sm text-primary-700 underline underline-offset-2"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </article>
      )}
    </div>
  );
}
